// context/GlobalLoadingContext.tsx
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useUISettings } from './UISettingsContext';

interface LoadingConfig {
  message?: string;
  messages?: string[];
  intervalMs?: number;
  messageDurationsMs?: number[];
  source?: string;
}

interface GlobalLoadingContextType {
  startLoading: (config?: LoadingConfig) => void;
  stopLoading: () => void;
  isLoading: boolean;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | null>(null);

export const GlobalLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { resolvedTheme } = useUISettings();
  const isDark = resolvedTheme === 'dark';
  const [loadingCount, setLoadingCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState<string[]>([]);
  const [messageIntervalMs, setMessageIntervalMs] = useState(1800);
  const [messageDurationsMs, setMessageDurationsMs] = useState<number[]>([]);
  const [messageIndex, setMessageIndex] = useState(0);
  const [visibleSinceMs, setVisibleSinceMs] = useState<number | null>(null);

  const MIN_VISIBLE_MS = 420;
  const HIDE_GRACE_MS = 260;

  const startLoading = useCallback((config?: LoadingConfig) => {
    setLoadingCount(prev => prev + 1);

    if (!config) return;

    const messages = config.messages?.filter(Boolean) ?? [];
    if (messages.length > 0) {
      setLoadingMessages(messages);
      setMessageIndex(0);
      setMessageIntervalMs(config.intervalMs ?? 1800);
      setMessageDurationsMs(config.messageDurationsMs ?? []);
      return;
    }

    if (config.message) {
      setLoadingMessages([config.message]);
      setMessageIndex(0);
      setMessageIntervalMs(config.intervalMs ?? 1800);
      setMessageDurationsMs([]);
      return;
    }
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingCount(prev => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    let hideTimer: number | null = null;

    if (loadingCount > 0) {
      if (!isVisible) {
        setIsVisible(true);
        setVisibleSinceMs(Date.now());
        window.dispatchEvent(new CustomEvent('global-loading-visible'));
      } else if (!visibleSinceMs) {
        setVisibleSinceMs(Date.now());
      }
      return;
    }

    if (!isVisible) {
      return;
    }

    const startedAt = visibleSinceMs ?? Date.now();
    const elapsed = Date.now() - startedAt;
    const remainingMinVisible = Math.max(MIN_VISIBLE_MS - elapsed, 0);
    const remaining = Math.max(remainingMinVisible, HIDE_GRACE_MS);

    hideTimer = window.setTimeout(() => {
      setIsVisible(false);
      setVisibleSinceMs(null);
      window.dispatchEvent(new CustomEvent('global-loading-hidden'));
    }, remaining);

    return () => {
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
      }
    };
  }, [loadingCount, isVisible, visibleSinceMs]);

  useEffect(() => {
    // Keep the last message visible during fade-out/grace period.
    // Clear only after the overlay is fully hidden to avoid textless spinner flashes.
    if (loadingCount !== 0 || isVisible) return;
    setLoadingMessages([]);
    setMessageDurationsMs([]);
    setMessageIndex(0);
  }, [loadingCount, isVisible]);

  useEffect(() => {
    if (loadingCount === 0 || loadingMessages.length <= 1) return;
    if (messageIndex >= loadingMessages.length - 1) return;

    const timeoutMs = messageDurationsMs[messageIndex] ?? messageIntervalMs;
    const timer = window.setTimeout(() => {
      setMessageIndex(prev => Math.min(prev + 1, loadingMessages.length - 1));
    }, timeoutMs);

    return () => window.clearTimeout(timer);
  }, [loadingCount, loadingMessages, messageIntervalMs, messageDurationsMs, messageIndex]);

  const currentMessage = useMemo(() => {
    if (loadingMessages.length === 0) return '';
    return loadingMessages[Math.min(messageIndex, loadingMessages.length - 1)];
  }, [loadingMessages, messageIndex]);

  return (
    <GlobalLoadingContext.Provider
      value={{ startLoading, stopLoading, isLoading: loadingCount > 0 }}
    >
      {children}

      {isVisible && (
        <div
          data-testid="global-loading-overlay"
          className="fixed inset-0 z-[9999] flex items-center justify-center px-3 sm:px-0"
          style={{
            background: isDark ? '#0f172a' : '#f8fafc',
          }}
        >
          <div
            className="flex w-full max-w-xl flex-col items-center gap-3 rounded-[1.25rem] px-5 py-6 text-center sm:w-auto sm:max-w-none sm:gap-4 sm:rounded-[2rem] sm:px-10 sm:py-10"
            style={
              isDark
                ? {
                    background: 'linear-gradient(180deg, rgba(15,23,42,0.9), rgba(15,23,42,0.84))',
                    border: '1px solid rgba(148, 163, 184, 0.26)',
                    boxShadow: '0 24px 64px -36px rgba(15,23,42,0.95)',
                  }
                : {
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    boxShadow: '0 24px 64px -36px rgba(15,23,42,0.34)',
                  }
            }
          >
            {currentMessage && (
              <p
                className="text-[10px] font-black uppercase tracking-[0.3em]"
                style={{ color: isDark ? 'rgba(191, 219, 254, 0.86)' : '#475569' }}
              >
                {currentMessage}
              </p>
            )}
            <div
              className="w-10 h-10 rounded-full border-4 border-t-transparent animate-spin"
              style={{
                borderColor: isDark ? 'rgba(129, 140, 248, 0.9)' : '#4f46e5',
                borderTopColor: 'transparent',
              }}
            />
          </div>
        </div>
      )}
    </GlobalLoadingContext.Provider>
  );
};

export const useGlobalLoading = () => {
  const ctx = useContext(GlobalLoadingContext);
  if (!ctx) throw new Error("useGlobalLoading must be used within provider");
  return ctx;
};
